// src/modules/accounting/components/ManualExpenseEntryModal.jsx
//
// Manual expense entry used from the Branch-wise Expense Report.
//
// One form covers the three ways an expense actually gets settled:
//   BRANCH CASH  → debits the branch's physical drawer (BranchCashLedger CASH_OUT)
//   FUND (bank)  → debits a Manage-Company-Funds fund and posts against its GL account
//   UNPAID       → records the liability only; pay it later from Expenses
//
// Attribution (which branch the spend belongs to) is deliberately independent of
// settlement: a company-wide expense can only be paid from a fund, but a branch
// expense may be paid either way. The Chart-of-Accounts code chosen here is the
// account that gets debited in the journal entry.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Form, Row, Col, Input, InputNumber, Select, DatePicker, Radio, Alert,
  Divider, Typography, Tag, Space, message,
} from 'antd';
import dayjs from 'dayjs';
import {
  createExpense, payExpense, fetchFunds, getAccounts,
} from '../api';
import {
  EXPENSE_CATEGORIES, EXPENSE_CATEGORY_MAP, EXPENSE_PAYMENT_MODES,
  DEFAULT_EXPENSE_ACCOUNT_CODE,
} from '../constants';
import { fetchBranches } from '../../branches/api';
import { fetchCashLedgerBalance } from '../../treasury/api';
import DenominationCountForm from '../../treasury/components/DenominationCountForm';

const { Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function ManualExpenseEntryModal({ open, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [branches, setBranches] = useState([]);
  const [funds, setFunds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [drawerBalance, setDrawerBalance] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [denomCounts, setDenomCounts] = useState({});

  const scope = Form.useWatch('scope', form);          // 'BRANCH' | 'COMPANY'
  const branchId = Form.useWatch('branch', form);
  const settlement = Form.useWatch('settlement', form); // 'DRAWER' | 'FUND' | 'UNPAID'
  const amount = Form.useWatch('amount', form);
  const taxAmount = Form.useWatch('taxAmount', form);
  const fundId = Form.useWatch('fundId', form);

  const total = (Number(amount) || 0) + (Number(taxAmount) || 0);

  /* ── Reference data ── */
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [bRes, fRes, aRes] = await Promise.all([
          fetchBranches({ limit: 1000 }),
          fetchFunds(),
          getAccounts({ type: 'EXPENSE', isActive: 'true' }),
        ]);
        setBranches(bRes?.data?.data || bRes?.data || []);
        setFunds(fRes?.data || []);
        setAccounts(aRes?.data || []);
      } catch {
        messageApi.error('Failed to load branches, funds or accounts');
      }
    })();
  }, [open, messageApi]);

  /* ── Reset on open ── */
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      scope: 'BRANCH',
      settlement: 'DRAWER',
      expenseDate: dayjs(),
      taxAmount: 0,
      paymentMode: 'BANK_TRANSFER',
      expenseAccountCode: DEFAULT_EXPENSE_ACCOUNT_CODE,
    });
    setDenomCounts({});
    setDrawerBalance(null);
  }, [open, form]);

  /* ── Live drawer balance for the selected branch ── */
  const loadDrawerBalance = useCallback(async (id) => {
    if (!id) { setDrawerBalance(null); return; }
    setDrawerLoading(true);
    try {
      const res = await fetchCashLedgerBalance({ branchId: id });
      const bal = res?.data?.data;
      setDrawerBalance(bal?.runningBalance ?? bal?.summary?.closingBalance ?? 0);
    } catch {
      setDrawerBalance(null);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && scope === 'BRANCH' && branchId) loadDrawerBalance(branchId);
    else setDrawerBalance(null);
  }, [open, scope, branchId, loadDrawerBalance]);

  /* ── A company-wide expense has no drawer to pay from ── */
  useEffect(() => {
    if (scope === 'COMPANY' && settlement === 'DRAWER') {
      form.setFieldsValue({ settlement: 'FUND' });
    }
  }, [scope, settlement, form]);

  const denomTotal = useMemo(
    () => Object.entries(denomCounts).reduce((s, [k, n]) => {
      const val = Number(String(k).replace('note', '')) || 0;
      return s + val * (Number(n) || 0);
    }, 0),
    [denomCounts],
  );

  const selectedFund = funds.find((f) => f._id === fundId);
  const insufficientDrawer = settlement === 'DRAWER' && drawerBalance !== null && total > 0 && drawerBalance < total;
  const insufficientFund = settlement === 'FUND' && selectedFund && total > 0 && (selectedFund.balance || 0) < total;

  const accountOptions = accounts.map((a) => ({
    value: a.code,
    label: `${a.code} — ${a.name}`,
  }));

  const onCategoryChange = (value) => {
    // Pre-select the account this category normally posts to; still fully editable.
    const suggested = EXPENSE_CATEGORY_MAP[value]?.account;
    if (suggested && accounts.some((a) => a.code === suggested)) {
      form.setFieldsValue({ expenseAccountCode: suggested });
    }
  };

  const handleSubmit = async (values) => {
    const branch = values.scope === 'BRANCH' ? values.branch : undefined;
    const payTotal = (Number(values.amount) || 0) + (Number(values.taxAmount) || 0);

    if (values.scope === 'BRANCH' && !branch) {
      messageApi.error('Select a branch, or switch to a company-wide entry');
      return;
    }
    if (values.settlement === 'DRAWER') {
      if (!branch) { messageApi.error('Branch cash can only be used for a branch expense'); return; }
      if (drawerBalance !== null && drawerBalance < payTotal) {
        messageApi.error(`Insufficient drawer cash (${fmt(drawerBalance)}) — pay from a bank fund instead, or fund the branch first`);
        return;
      }
      if (denomTotal > 0 && Math.round(denomTotal) !== Math.round(payTotal)) {
        messageApi.error(`Denomination total (${fmt(denomTotal)}) must equal the expense amount (${fmt(payTotal)})`);
        return;
      }
    }
    if (values.settlement === 'FUND' && !values.fundId) {
      messageApi.error('Select the fund the money leaves from');
      return;
    }

    const base = {
      ...(branch ? { branch } : {}),
      category: values.category,
      subCategory: values.subCategory,
      description: values.description,
      amount: Number(values.amount) || 0,
      taxAmount: Number(values.taxAmount) || 0,
      expenseDate: values.expenseDate?.toISOString(),
      payeeName: values.payeeName,
      expenseAccountCode: values.expenseAccountCode,
      status: 'APPROVED',
      remarks: values.remarks,
    };

    setSubmitting(true);
    try {
      if (values.settlement === 'FUND') {
        // Create + debit the fund in one atomic server-side transaction.
        await createExpense({
          ...base,
          sourceFundId: values.fundId,
          paymentMode: values.paymentMode,
          transactionReference: values.transactionReference,
        });
        messageApi.success(`Expense recorded and paid from ${selectedFund?.name || 'fund'}`);
      } else if (values.settlement === 'DRAWER') {
        // Two steps — the drawer debit needs an expense to hang off. If the payment
        // leg fails (closed business day, drawer short), the expense survives as
        // APPROVED-unpaid, so say so rather than leaving a silent orphan.
        const created = await createExpense({ ...base, paymentMode: 'CASH' });
        const id = created?.data?._id;
        if (!id) throw new Error('Expense was created but no id was returned');
        try {
          await payExpense(id, {
            source: 'DRAWER',
            businessDate: values.expenseDate?.toISOString(),
            ...(denomTotal > 0 ? { denominations: denomCounts } : {}),
            remarks: values.remarks,
          });
        } catch (payErr) {
          const reason = payErr.response?.data?.message || 'the drawer payment failed';
          messageApi.error(`${created.data.expenseId} was recorded but not paid — ${reason}. Settle it from Accounting → Expenses.`);
          onSaved?.();
          onClose?.();
          return;
        }
        messageApi.success('Expense recorded and paid from the branch cash drawer');
      } else {
        await createExpense({ ...base, paymentMode: values.paymentMode });
        messageApi.success('Expense recorded — still unpaid');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      messageApi.error(e.response?.data?.message || e.message || 'Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Manual Expense Entry"
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Record Expense"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* ── Attribution ── */}
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item
                label="Attribution"
                name="scope"
                tooltip="Which side of the business carries this cost. Drives the branch-wise report."
              >
                <Radio.Group>
                  <Radio.Button value="BRANCH">Branch</Radio.Button>
                  <Radio.Button value="COMPANY">Whole Company</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={14}>
              {scope === 'BRANCH' ? (
                <Form.Item label="Branch" name="branch" rules={[{ required: true, message: 'Select a branch' }]}>
                  <Select
                    placeholder="Select branch"
                    showSearch
                    optionFilterProp="label"
                    options={branches.map((b) => ({ value: b._id, label: `${b.name}${b.code ? ` (${b.code})` : ''}` }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item label=" ">
                  <Text type="secondary">
                    Company-wide — reported under “Unassigned”, payable only from a fund.
                  </Text>
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* ── What was spent ── */}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select
                  options={EXPENSE_CATEGORIES}
                  placeholder="Select category"
                  showSearch
                  optionFilterProp="label"
                  onChange={onCategoryChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true, message: 'Enter the amount' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Tax (₹)" name="taxAmount" tooltip="Added to the amount to give the total paid.">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description" rules={[{ required: true }]}>
            <Input placeholder="What was this spent on?" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Payee" name="payeeName">
                <Input placeholder="Vendor / landlord / person paid" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Sub-category" name="subCategory">
                <Input placeholder="e.g. Diesel, Google Ads" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Date" name="expenseDate" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Ledger account ── */}
          <Form.Item
            label="Expense Account (Chart of Accounts)"
            name="expenseAccountCode"
            rules={[{ required: true, message: 'Pick the account to debit' }]}
            tooltip="The account debited in the journal entry. Pre-filled from the category — change it to post anywhere in the expense ledger."
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select expense account"
              options={accountOptions}
              notFoundContent={accounts.length ? 'No match' : 'No expense accounts found — bootstrap the Chart of Accounts first'}
            />
          </Form.Item>

          <Divider plain style={{ margin: '4px 0 12px' }}>Settlement</Divider>

          <Form.Item
            name="settlement"
            tooltip="Where the money actually comes from. Branch cash debits the physical drawer; a fund debits the bank/HO balance."
          >
            <Radio.Group>
              <Radio.Button value="DRAWER" disabled={scope !== 'BRANCH'}>Branch Cash Drawer</Radio.Button>
              <Radio.Button value="FUND">Bank / Fund</Radio.Button>
              <Radio.Button value="UNPAID">Record only (unpaid)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {settlement === 'DRAWER' && (
            <>
              <Alert
                type={insufficientDrawer ? 'error' : 'info'}
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  drawerLoading ? 'Loading drawer balance…'
                    : drawerBalance === null ? 'Select a branch to see its drawer balance'
                      : `Drawer cash available: ${fmt(drawerBalance)} · this expense: ${fmt(total)}`
                }
                description={
                  insufficientDrawer
                    ? 'Not enough cash in the drawer. Switch to “Bank / Fund” and pick the fund it was paid from, or record it unpaid and settle later.'
                    : 'Cash leaves the branch drawer on this date — the branch business day must be open.'
                }
              />
              <Divider plain style={{ margin: '4px 0 12px' }}>
                Denomination breakdown (optional — must equal {fmt(total)})
              </Divider>
              <DenominationCountForm
                counts={denomCounts}
                onChange={setDenomCounts}
                expectedCash={total}
              />
            </>
          )}

          {settlement === 'FUND' && (
            <>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Fund (Manage Company Funds)" name="fundId" rules={[{ required: true, message: 'Select a fund' }]}>
                    <Select
                      placeholder="Choose the fund to debit"
                      showSearch
                      optionFilterProp="label"
                      options={funds.map((f) => ({
                        value: f._id,
                        label: `${f.name} — ${fmt(f.balance)}`,
                      }))}
                      optionRender={(opt) => {
                        const fund = funds.find((f) => f._id === opt.value);
                        return (
                          <Space>
                            <span>{fund?.name}</span>
                            <Text type="secondary">{fmt(fund?.balance)}</Text>
                            {fund?.linkedAccountCode && <Tag>{fund.linkedAccountCode}</Tag>}
                            {total > 0 && (fund?.balance || 0) < total && <Tag color="red">Insufficient</Tag>}
                          </Space>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Payment Mode" name="paymentMode" rules={[{ required: true }]}>
                    <Select options={EXPENSE_PAYMENT_MODES.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Reference" name="transactionReference">
                    <Input placeholder="UTR / cheque #" />
                  </Form.Item>
                </Col>
              </Row>
              {insufficientFund && (
                <Alert
                  type="error" showIcon style={{ marginBottom: 12 }}
                  message={`${selectedFund.name} holds ${fmt(selectedFund.balance)} — less than the ${fmt(total)} being paid.`}
                />
              )}
              {selectedFund?.linkedAccountCode && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Credits GL {selectedFund.linkedAccountCode} — the account this fund is linked to.
                </Text>
              )}
            </>
          )}

          {settlement === 'UNPAID' && (
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message="Recorded as APPROVED but unpaid — no money moves and nothing posts to the ledger yet."
              description="Settle it later from Accounting → Expenses, which will debit the drawer or fund you choose then."
            />
          )}

          <Form.Item label="Remarks" name="remarks" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} placeholder="Optional note kept on the expense record" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
